import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { DeltaResponse, InsuredOnDateResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  PageHeader,
  Panel,
  TextField,
  type AsyncState,
  type Column,
} from '../../components';
import {
  formatCpf,
  formatCurrency,
  formatDate,
  formatInteger,
  stripCpf,
  todayInAppTimeZone,
} from '../../format';
import { PATHS } from '../../routes';
import {
  useApprovedFutureDeltas,
  usePendingDeltas,
  usePortalContracts,
  useRoster,
} from '../queries';
import { useCompanyScope } from './companyScope';
import {
  ACTION_LABELS,
  PORTAL_COPY,
  STATUS_LABELS,
  nameForChange,
  primaryScheduledChange,
  scheduledByCpf,
  scheduledChanges,
  searchRoster,
} from './portal';

/**
 * Who is insured, today, under this company's contract.
 *
 * Two things on this screen are not obvious from the endpoint it reads.
 *
 * **The search runs in the browser.** `GET /contracts/{id}/roster` returns the
 * projected population and takes no search parameter, and adding one would be a
 * change to #207 rather than to the portal. Filtering what has already arrived
 * is honest for a contract of the size these are, and it costs no round trip
 * per keystroke -- which on a field carrying a CPF also means no partial CPF
 * ever reaches a query string, a proxy log or an access log.
 *
 * **A person with something coming is marked.** The projection for today cannot
 * distinguish somebody who will still be here next month from somebody whose
 * termination is already filed for the 31st -- both are simply insured today.
 * That is exactly the confusion that makes an HR user file the same termination
 * a second time in April, so the scheduled changes are read alongside the
 * roster and shown on the row.
 */

function personPath(person: InsuredOnDateResponse): string {
  return `${PATHS.customerBeneficiaries}/${person.insured_person_id}`;
}

function columnsFor(
  scheduled: ReadonlyMap<string, readonly DeltaResponse[]>,
): readonly Column<InsuredOnDateResponse>[] {
  return [
    {
      key: 'full_name',
      header: 'Nome',
      render: (person) => <Link to={personPath(person)}>{person.full_name}</Link>,
    },
    { key: 'cpf', header: 'CPF', render: (person) => <code>{formatCpf(person.cpf)}</code> },
    {
      key: 'birth_date',
      header: 'Nascimento',
      align: 'end',
      render: (person) => formatDate(person.birth_date),
    },
    {
      key: 'salary',
      header: 'Salário',
      align: 'end',
      render: (person) => formatCurrency(person.salary),
    },
    {
      key: 'coverage_from',
      header: 'Início da cobertura',
      align: 'end',
      render: (person) => formatDate(person.coverage_from),
    },
    {
      key: 'coverage_to',
      header: 'Fim da cobertura',
      align: 'end',
      render: (person) => formatDate(person.coverage_to),
    },
    {
      key: 'scheduled',
      header: 'Movimentação agendada',
      render: (person) => {
        const changes = scheduled.get(stripCpf(person.cpf)) ?? [];
        const primary = primaryScheduledChange(changes);
        if (primary === null) {
          return <span className="table__muted">—</span>;
        }
        return (
          <>
            {/* Both halves read off the same delta. See primaryScheduledChange. */}
            <span className="tag tag--scheduled">
              {ACTION_LABELS[primary.action]} em {formatDate(primary.effective_date)}
            </span>
            {changes.length > 1 && (
              // The row shows one; saying how many there are is what stops it
              // being read as the whole story. The panel below lists them all.
              <span className="table__muted">
                {' '}
                e mais {formatInteger(changes.length - 1)} agendada(s)
              </span>
            )}
          </>
        );
      },
    },
  ];
}

function scheduledColumns(
  roster: readonly InsuredOnDateResponse[],
): readonly Column<DeltaResponse>[] {
  return [
    { key: 'action', header: 'Movimentação', render: (delta) => ACTION_LABELS[delta.action] },
    {
      key: 'full_name',
      header: 'Pessoa',
      render: (delta) => nameForChange(delta, roster),
    },
    { key: 'cpf', header: 'CPF', render: (delta) => <code>{formatCpf(delta.cpf)}</code> },
    {
      key: 'effective_date',
      header: 'Vigência',
      align: 'end',
      render: (delta) => formatDate(delta.effective_date),
    },
    { key: 'status', header: 'Situação', render: (delta) => STATUS_LABELS[delta.status] },
  ];
}

export function RosterPage() {
  const { company } = useCompanyScope();
  const contracts = usePortalContracts(company.id);
  // A customer has one contract today. `noUncheckedIndexedAccess` makes the
  // "or none yet" case something every read below has to handle, which is
  // what keeps a company still being onboarded off a broken screen.
  const contract = contracts.data?.items[0];
  const [asOf] = useState(todayInAppTimeZone);
  const [query, setQuery] = useState('');

  const roster = useRoster(company.id, contract?.id, asOf);
  const pending = usePendingDeltas(company.id, contract?.id);
  const approvedFuture = useApprovedFutureDeltas(company.id, contract?.id, asOf);

  const scheduled = useMemo(
    () =>
      scheduledChanges(
        [...(pending.data?.items ?? []), ...(approvedFuture.data?.items ?? [])],
        asOf,
      ),
    [pending.data, approvedFuture.data, asOf],
  );
  const scheduledIndex = useMemo(() => scheduledByCpf(scheduled), [scheduled]);
  const people = useMemo(() => searchRoster(roster.data?.items ?? [], query), [roster.data, query]);

  /**
   * The two delta reads, as one state.
   *
   * They answer one question between them -- what has been asked for and not
   * happened yet -- so reporting only the first would leave the panel looking
   * complete while half of it was missing.
   */
  const scheduledFailed = pending.isError || approvedFuture.isError;
  const scheduledState: AsyncState<readonly DeltaResponse[]> = {
    isPending: pending.isPending || approvedFuture.isPending,
    isError: scheduledFailed,
    error: pending.error ?? approvedFuture.error,
    data: scheduledFailed ? undefined : scheduled,
    refetch: () => {
      void pending.refetch();
      void approvedFuture.refetch();
    },
  };

  return (
    <div className="page">
      <PageHeader
        title={PORTAL_COPY.rosterTitle}
        description={`${PORTAL_COPY.rosterDescription} Posição em ${formatDate(asOf)}.`}
      />

      <AsyncSection
        state={contracts}
        loadingLabel="Carregando contrato…"
        errorTitle="Não foi possível carregar o contrato"
        isEmpty={(data) => data.items.length === 0}
        emptyTitle="Nenhum contrato"
        emptyDescription={PORTAL_COPY.noContract}
      >
        {() => (
          <>
            <Panel title={PORTAL_COPY.rosterPanelTitle}>
              <TextField
                label={PORTAL_COPY.searchLabel}
                hint={PORTAL_COPY.searchHint}
                value={query}
                onChange={setQuery}
                autoComplete="off"
              />

              <AsyncSection
                state={roster}
                loadingLabel="Carregando beneficiários…"
                errorTitle="Não foi possível carregar os beneficiários"
                isEmpty={(data) => data.items.length === 0}
                emptyTitle={PORTAL_COPY.emptyRoster}
                emptyDescription={PORTAL_COPY.emptyRosterHint}
              >
                {(data) => (
                  <>
                    <DataTable
                      id="customer-roster"
                      caption="Pessoas cobertas pelo contrato nesta data"
                      columns={columnsFor(scheduledIndex)}
                      rows={people}
                      rowKey={(person) => person.insured_person_id}
                      emptyTitle={PORTAL_COPY.noMatches}
                      emptyDescription={PORTAL_COPY.noMatchesHint}
                    />
                    <p className="panel__footnote">
                      {formatInteger(people.length)} de {formatInteger(data.count)} pessoa(s).
                    </p>
                    {scheduledFailed && (
                      // Without this the page would silently understate itself:
                      // the markers come from a read that failed, so somebody
                      // with an exclusion already filed would look ordinary,
                      // which is precisely the mistake the markers exist to stop.
                      <p className="panel__footnote" role="alert">
                        {PORTAL_COPY.scheduledUnavailable}
                      </p>
                    )}
                  </>
                )}
              </AsyncSection>
            </Panel>

            <Panel
              title={PORTAL_COPY.scheduledTitle}
              description={PORTAL_COPY.scheduledDescription}
            >
              <AsyncSection
                state={scheduledState}
                loadingLabel="Carregando movimentações agendadas…"
                errorTitle="Não foi possível carregar as movimentações agendadas"
              >
                {(rows) => (
                  <DataTable
                    id="customer-scheduled-movements"
                    caption="Movimentações já pedidas e ainda por acontecer"
                    columns={scheduledColumns(roster.data?.items ?? [])}
                    rows={rows}
                    rowKey={(delta) => delta.id}
                    emptyTitle={PORTAL_COPY.noScheduled}
                  />
                )}
              </AsyncSection>
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}
